import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loading, Empty } from "./_shared";

export function ExitChecklistDialog({ separation, employeeName, onChanged, trigger }) {
  const [open, setOpen] = useState(false);
  const [sep, setSep] = useState(separation);
  const [busy, setBusy] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("equipment");

  useEffect(() => { if (open) setSep(separation); }, [open, separation]);

  const refresh = async () => {
    const { data } = await api.get("/hr/separations");
    const updated = (data.items || []).find((s) => s.id === separation.id);
    if (updated) setSep(updated);
    onChanged?.();
  };

  const toggleItem = async (item, done) => {
    try {
      await api.post(`/hr/separations/${sep.id}/checklist/toggle`,
        { item_id: item.id, done, note: item.note || "" });
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addCustomItem = async (e) => {
    e.preventDefault();
    if (!newItemTitle.trim()) return;
    setBusy(true);
    try {
      await api.post(`/hr/separations/${sep.id}/checklist/add`, {
        title: newItemTitle.trim(), category: newItemCategory, required: true,
      });
      setNewItemTitle("");
      await refresh();
      toast.success("Item added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeCustom = async (itemId) => {
    if (!window.confirm("Remove this custom checklist item?")) return;
    try {
      await api.delete(`/hr/separations/${sep.id}/checklist/${itemId}`);
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/separations/${sep.id}/finalize`);
      toast.success("Exit procedure finalized");
      await refresh();
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const items = sep.exit_checklist || [];
  const requiredDone = items.filter((i) => i.required && i.done).length;
  const requiredTotal = items.filter((i) => i.required).length;
  const allDone = requiredTotal > 0 && requiredDone === requiredTotal;
  const pct = items.length ? Math.round(items.filter((i) => i.done).length / items.length * 100) : 0;
  const isClosed = sep.status === "closed";

  const byCategory = items.reduce((acc, it) => {
    const c = it.category || "general"; (acc[c] = acc[c] || []).push(it); return acc;
  }, {});
  const CAT_LABELS = {
    equipment: "Equipment Return", access: "Access & Credentials",
    knowledge: "Knowledge Transfer", finance: "Finance Settlement",
    general: "General",
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            Exit Procedure · {employeeName}
            {isClosed && <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-mono-alt uppercase tracking-wider text-emerald-700">closed</span>}
          </DialogTitle>
          <DialogDescription>
            {sep.separation_type?.replace(/_/g, " ")} · effective {formatDate(sep.effective_date)} · reason: {sep.reason}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between text-xs font-mono-alt uppercase tracking-wider text-slate-600 mb-1.5">
            <span>Overall checklist progress</span>
            <span className="tabular-nums font-bold">{pct}% · {requiredDone}/{requiredTotal} required</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-white">
            <div className={`h-full ${allDone ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>

        {Object.entries(byCategory).map(([cat, list]) => (
          <div key={cat} className="space-y-2">
            <div className="text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">{CAT_LABELS[cat] || cat}</div>
            <ul className="space-y-1.5">
              {list.map((it) => (
                <li key={it.id}
                    className={`flex items-start gap-3 rounded-md border p-3 text-sm ${
                      it.done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"
                    }`}>
                  <input
                    type="checkbox" checked={!!it.done} disabled={isClosed}
                    onChange={(e) => toggleItem(it, e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer accent-emerald-600"
                    data-testid={`exit-item-${it.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`font-medium ${it.done ? "text-slate-500 line-through" : "text-slate-900"}`}>{it.title}</span>
                      {it.required && <span className="font-mono-alt text-[9px] uppercase tracking-wider text-red-700">required</span>}
                      {it.custom && <span className="font-mono-alt text-[9px] uppercase tracking-wider text-indigo-700">custom</span>}
                    </div>
                    {it.done && it.done_by && (
                      <div className="mt-0.5 text-[10px] text-slate-500">✓ {it.done_by} · {formatDate(it.done_at)}</div>
                    )}
                  </div>
                  {it.custom && !isClosed && (
                    <button type="button" onClick={() => removeCustom(it.id)}
                            className="text-xs text-red-600 hover:underline" data-testid={`remove-${it.id}`}>
                      remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {!isClosed && (
          <form onSubmit={addCustomItem} className="rounded-lg border border-dashed border-slate-300 p-3 space-y-2">
            <div className="text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">Add custom item (extra equipment, pending job, anything else)</div>
            <div className="flex gap-2">
              <Input placeholder="e.g. Return drone DJI Mavic 3, finish PR-2026-040 inspection report" value={newItemTitle}
                     onChange={(e) => setNewItemTitle(e.target.value)} className="flex-1" data-testid="new-checklist-title" />
              <Select value={newItemCategory} onValueChange={setNewItemCategory}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="access">Access</SelectItem>
                  <SelectItem value="knowledge">Knowledge</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" disabled={busy || !newItemTitle.trim()} data-testid="add-checklist-item">Add</Button>
            </div>
          </form>
        )}

        <DialogFooter>
          {!isClosed && (
            <Button onClick={finalize} disabled={busy || !allDone}
                    className="btn-primary-blue" data-testid="finalize-separation"
                    title={allDone ? "Mark exit procedure as complete" : "All required items must be ticked before finalizing"}>
              {busy ? "Finalizing…" : allDone ? "✓ Finalize exit procedure" : `${requiredTotal - requiredDone} required item(s) pending`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Separations({ staff }) {
  const staffName = (id) => staff.find((x) => x.id === id)?.full_name || id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    staff_id: "", separation_type: "resignation", effective_date: "",
    reason: "", final_dues: 0,
  });
  const [filter, setFilter] = useState("in_progress");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/separations");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hr/separations", form);
      toast.success("Separation initiated · exit checklist seeded");
      setCreateOpen(false);
      setForm({ staff_id: "", separation_type: "resignation", effective_date: "", reason: "", final_dues: 0 });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = items.filter((s) => {
    const status = s.status || "in_progress";
    if (filter === "all") return true;
    return status === filter;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
            {[
              ["in_progress", "In progress", items.filter((s) => (s.status || "in_progress") === "in_progress").length],
              ["closed", "Closed", items.filter((s) => s.status === "closed").length],
              ["all", "All", items.length],
            ].map(([k, l, n]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                      data-testid={`sep-filter-${k}`}
                      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm transition ${
                        filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                      }`}>
                {l} <span className={`ml-1.5 inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 text-[10px] ${
                  filter === k ? "bg-white/20" : "bg-slate-100 text-slate-600"
                }`}>{n}</span>
              </button>
            ))}
          </div>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary-blue" data-testid="new-separation">
              <Plus size={14} className="mr-2" /> New separation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">New separation</DialogTitle>
              <DialogDescription>A 14-item exit checklist will be auto-created. You can add custom items inside.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Employee *</Label>
                <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select employee…" /></SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.employee_code || "—"})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Type *</Label>
                <Select value={form.separation_type} onValueChange={(v) => setForm({ ...form, separation_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resignation">Resignation</SelectItem>
                    <SelectItem value="termination">Termination</SelectItem>
                    <SelectItem value="end_of_contract">End of Contract</SelectItem>
                    <SelectItem value="retirement">Retirement</SelectItem>
                    <SelectItem value="suspension">Suspension</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Effective Date *</Label><Input type="date" required value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
              <div><Label>Reason *</Label><Textarea required rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              <div><Label>Final Dues (USD)</Label><Input type="number" value={form.final_dues} onChange={(e) => setForm({ ...form, final_dues: Number(e.target.value) })} /></div>
              <DialogFooter>
                <Button type="submit" disabled={!form.staff_id} className="btn-primary-blue">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : filtered.length === 0 ? (
          <Empty text={filter === "in_progress" ? "No exits in progress." : filter === "closed" ? "No closed separations yet." : "No separations on record."} />
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3">Employee</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Effective</th>
                <th className="px-5 py-3">Progress</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((s) => {
                const checklist = s.exit_checklist || [];
                const done = checklist.filter((i) => i.done).length;
                const pct = checklist.length ? Math.round(done / checklist.length * 100) : 0;
                const isClosed = s.status === "closed";
                return (
                  <tr key={s.id} className="text-sm" data-testid={`sep-row-${s.id}`}>
                    <td className="px-5 py-3 font-semibold text-slate-900">{staffName(s.staff_id)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs uppercase tracking-wider">{s.separation_type?.replace(/_/g, " ")}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{formatDate(s.effective_date)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full ${isClosed ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono-alt text-xs tabular-nums">{done}/{checklist.length}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider ${
                        isClosed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"
                      }`}>{isClosed ? "Closed" : "In progress"}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <ExitChecklistDialog
                        separation={s}
                        employeeName={staffName(s.staff_id)}
                        onChanged={load}
                        trigger={
                          <Button size="sm" variant={isClosed ? "outline" : "default"}
                                  className={!isClosed ? "btn-primary-blue" : ""}
                                  data-testid={`open-exit-${s.id}`}>
                            {isClosed ? "View checklist" : "Open exit procedure"}
                          </Button>
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
